<script setup lang="ts">
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import DropdownArrow from "~/components/icons/dropdown-arrow/DropdownArrow.vue";
import ErrorMessage from "~/components/ui/ErrorMessage.vue";
import type { IValue } from "~/interfaces";

defineComponent({
  name: "SelectNew",
});

const props = defineProps({
  name: {
    type: String,
    default: () => null,
    required: false,
  },
  values: {
    type: Array as () => IValue[],
    default: () => [],
    required: true,
  },
  disableZeroValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  floating: {
    type: Boolean,
    required: false,
    default: () => false,
  },
  label: {
    type: String,
    required: false,
    default: "",
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  error: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: String,
    required: false,
    default: "Seleziona un'opzione",
  },
  testId: {
    type: String,
    default: "",
  },
  spacers: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMargin: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["change"]);
const value = defineModel<any>();
const uid = getCurrentInstance()?.uid;
const isMobile = ref(false);

const listboxButtonRef = useTemplateRef('listbox-button');

const isFakeFocused = ref(false);

const triggerFocus = () => {
  const el = listboxButtonRef.value?.$el as HTMLElement | undefined;
  if (el) {
    el.focus();
    isFakeFocused.value = true;
  }
};

const handleBlur = () => {
  isFakeFocused.value = false;
};

defineExpose({ 
  focus: triggerFocus
})

const preventBodyScroll = (e: TouchEvent) => {
  const target = e.target as HTMLElement;
  if (target.closest('.mobile-dropdown-list')) return;
  e.preventDefault();
};

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

const shouldTeleport = ref(false);

onMounted(() => {
  checkMobile();
  shouldTeleport.value = !!document.querySelector('.modal-wrapper');
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  document.body.style.overflow = '';
  document.removeEventListener('touchmove', preventBodyScroll);
});

const getArrowClass = (open: boolean) =>
  [
    'absolute top-2.5 right-2 text-gray-900 pointer-events-none transition-transform duration-500',
    open ? 'rotate-180' : ''
  ].filter(Boolean).join(' ');


const listboxOpen = ref(false);

const hasValue = computed(() => 
  value.value !== null && 
  value.value !== undefined && 
  value.value !== props.values[0]?.value
);

const displayLabel = computed(() => {
  if (props.floating && (value.value === null || value.value === undefined)) {
    return '';
  }
  if (value.value === null || value.value === undefined) {
    return props.values[0]?.label;
  }
  return props.values.find(v => v.value === value.value)?.label || props.values[0]?.label;
});

watch(listboxOpen, (newVal) => {
  if (isMobile.value) {
    if (newVal) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('touchmove', preventBodyScroll, { passive: false });
    } else {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventBodyScroll);
    }
  }
});

const getTeleportedPosition = () => {
  const button = document.querySelector(`#select-${uid}`) as HTMLElement;
  if (!button) return {};
  
  const rect = button.getBoundingClientRect();
  return {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  };
};
</script>

<template>
  <div 
  
    :class="[
      'select primary', 
      props.floating && 'label-floating',
      (props.error && props.errorMargin) ? 'mb-8 md:mb-4' : ''
    ]"  
    
    >
    <div
      :class="[
        'form-select',
        hasValue ? 'has-value' : 'no-value',
        listboxOpen && 'open'
      ]"
    >
      <Listbox v-model="value" :disabled="props.disabled" @update:modelValue="emit('change')" class="w-full">
        <div class="relative">
          <ListboxButton
            v-slot="{ open }"
            ref="listbox-button"
            @blur="handleBlur" 
            :class="[
              $attrs.class,
              props.floating ? 'floating-select select-trigger' : 'normal-select text-left',
              hasValue ? 'has-value' : 'no-value',
              props.error ? 'error' : '',
              props.disabled ? 'disabled-bg' : '',
              'py-2 px-6',
              isFakeFocused ? 'fake-focus' : ''
            ]"
            :data-testid="props.testId"
            :id="`select-${uid}`"
            :aria-labelledby="props.floating ? `select-${uid}-label` : undefined"
            :aria-label="!props.floating && props.label ? props.label : undefined"
          >
            <template v-if="open !== listboxOpen">{{ listboxOpen = open }}</template>
            <span class="block truncate" :class="!props.floating && ''">
              {{ displayLabel }}
            </span>
            <DropdownArrow 
              :class="getArrowClass(open)"
              size="1.66rem"
            />
          </ListboxButton>

          <!-- Mobile Overlay -->
          <Teleport to="body">
          <div 
            v-if="isMobile && listboxOpen"
            class="mobile-overlay"
            @click.self="listboxOpen = false"
          >
            <ListboxOptions class="mobile-options-wrapper w-full">            
              <div class="mobile-dropdown-list ">
                <div class="mobile-header">
                  <h3>{{ props.values[0]?.label }}</h3>
                </div>
                <div class="mobile-options-container">
                  <template v-for="(option, i) in props.values" :key="i">
                    <ListboxOption
                      v-if="i > 0"
                      :key="`${i+1}_${option.value}`"
                      :value="option.value"
                      :disabled="option.disabled"
                      v-slot="{ active, selected }"
                      as="template"
                    >
                      <li 
                        :class="[
                          'dropdown-option mobile text-center',
                          active && 'highlighted',
                          selected && 'selected',
                          option.disabled && 'disabled'
                        ]"
                        :aria-disabled="option.disabled ? 'true' : undefined"
                      >
                        {{ option.label }}
                      </li>
                    </ListboxOption>
                    <div 
                      v-if="props.spacers && i < props.values.length - 1 && i > 0" 
                      class="h-px bg-gray-200 mx-2"
                    ></div>
                  </template>
                </div>
              </div>
            </ListboxOptions>
          </div>
          </Teleport>

          <!-- Desktop Dropdown -->
          <Teleport to="body" :disabled="!shouldTeleport">
            <ListboxOptions 
              v-if="!isMobile" 
              :class="['custom-dropdown-list', shouldTeleport && 'teleported']"
              :style="shouldTeleport ? getTeleportedPosition() : {}"
            >
              <template v-for="(option, i) in props.values" :key="`${i}_${option.value}`">
                <ListboxOption
                  :value="option.value"
                  :disabled="(props.disableZeroValue && i === 0) || option.disabled"
                  v-slot="{ active, selected }"
                  as="template"
                >
                  <li 
                    :class="[
                      'dropdown-option',
                      active && 'highlighted',
                      selected && 'selected',
                      ((props.disableZeroValue && i === 0) || option.disabled) && 'disabled'
                    ]"
                    :aria-disabled="(props.disableZeroValue && i === 0) ? 'true' : undefined"
                  >
                    {{ option.label }}
                  </li>
                </ListboxOption>
                <div v-if="props.spacers && i < props.values.length - 1 && i > 0" class="h-px bg-gray-200 mx-2" />
              </template>
            </ListboxOptions>
          </Teleport>
        </div>
      </Listbox>

      <select
        v-model="value"
        :name="props.name"
        :disabled="props.disabled"
        class="hidden-native-select"
        tabindex="-1"
        aria-hidden="true"
        :aria-label="props.label"
      >
        <option
          v-for="(option, i) in props.values"
          :key="`${i}_${option.value}`"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <label
      v-if="props.floating"
      :for="`select-${uid}`"
      :class="[
        'floating-label',
        hasValue || listboxOpen ? 'active' : ''
      ]"
      :id="`select-${uid}-label`"
    >
      {{ props.label }}
    </label>
    <ErrorMessage v-if="props.error && props.errorMessage" :message="props.errorMessage" />
  </div>
</template>

<style scoped lang="scss">
.hidden-native-select {
  display: none !important;
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}
/* Mobile Overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  animation: fadeIn 0.2s ease;
  cursor: pointer;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.mobile-options-wrapper,
.mobile-dropdown-list {
  background: white;
  border-radius: 1.5rem 1.5rem 0 0;
  width: 100%;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  cursor: default;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.mobile-header {
  padding: 1.25rem 1.5rem;
  // border-bottom: 1px solid var(--less-light-yellow);
  position: sticky;
  top: 0;
  background: white;
  border-radius: 1.5rem 1.5rem 0 0;
  z-index: 1;

  h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    text-align: center;
  }
}

.mobile-options-container {
  overflow-y: auto;
  padding: 0 1rem 1rem 1rem;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0.5rem 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    border: 2px solid white;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--yellow);
  }
}

/* Desktop Dropdown */
.custom-dropdown-list {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  z-index: 50;
  max-height: 16rem;
  overflow-y: auto;
  background: white;
  border: 1px solid var(--yellow);
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 0.25rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0.5rem 0;

  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
    border: 2px solid white; 
  }

  // &::-webkit-scrollbar-thumb:hover {
  //   background: var(--yellow);
  // }
  &.teleported {
    position: fixed;
    z-index: 10000;
  }
}

.dropdown-option {
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background-color 0.15s ease;
  font-size: 1rem;

  &.highlighted {
    background-color: #f3f4f6;
  }

  &.selected {
    background-color: var(--less-light-yellow);
    font-weight: 600;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.mobile {
    padding: 1rem 0.75rem;
  }
}

.select-trigger {
  @apply cursor-pointer text-sm sm:text-base rounded-full px-4 w-full py-2 leading-normal h-12 border border-gray-6 bg-white text-left;
  display: block;
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:focus {
    outline: none !important;
    border-color: var(--yellow);
    box-shadow: 0 0 0 0.3px var(--yellow);
  }
}

.fake-focus {
  outline: none !important;
  border-color: var(--yellow) !important;
  box-shadow: 0 0 0 0.3px var(--yellow) !important; 
  transition: all 0.2s ease;
  
}


.select-trigger.disabled-bg {
  @apply bg-gray-100 cursor-not-allowed border-gray-400;
}

.select-trigger.disabled-bg svg {
  background: inherit;
}

.select-trigger.has-value {
  padding-top: 1.1rem;
}

.select-trigger:not(.has-value) {
  padding-top: 0.75rem !important;
}

.select-trigger.error {
  @apply border-red-1;
}

.label-floating {
  position: relative;
}

.label-floating .floating-label {
  @apply text-gray-6 absolute left-[1px] block px-4 leading-normal transition-all ease-in-out duration-200 pointer-events-none;
  width: 100%;
  top: 12px;
  font-size: 1rem;
  z-index: 30;
  background: transparent;
}

.form-select {
  @apply relative;
}


.form-select:focus-within .select-trigger {
  @apply border-yellow shadow-md;
}


.select-trigger svg {
  background: white;
  border-radius: 9999px;
}


.floating-label {
  top: 12px;
  font-size: 1rem;
  transition: all .2s ease;
}

.floating-label.active {
  top: 4px !important;
  font-size: 0.6rem !important;
  padding: 0 0.25rem;
  left: 0.75rem !important;
  z-index: 20;
}
</style>
